<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { generateStatisticsFileName } from 'dashboard/helper/downloadHelper';
import { REPORTS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import ReportFiltersSection from './ReportFiltersSection.vue';
import SummaryMetricCards from './SummaryMetricCards.vue';
import ChatsReportCharts from './ChatsReportCharts.vue';
import ChatsTimeSummaryTable from './ChatsTimeSummaryTable.vue';
import ChatsDailyDetailTable from './ChatsDailyDetailTable.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { parseSummaryEntityFilterParams } from '../helpers/reportFilterHelper';

const store = useStore();
const route = useRoute();
const { t } = useI18n();

const from = ref(0);
const to = ref(0);
const businessHours = ref(false);
const selectedLabelIds = ref([]);
const excludeLabelIds = ref([]);
const selectedInboxIds = ref([]);
const selectedTeamIds = ref([]);
const selectedAgentIds = ref([]);
const resolutionMethod = ref('all');
const statusAtPeriodEnd = ref('all');
const reopening = ref('all');
const transfers = ref('all');
const humanResponse = ref('all');
const comparisonPeriods = ref([]);
const dailyDynamics = ref([]);

const applyEntityFilters = filters => {
  selectedAgentIds.value = filters.agentIds || [];
  selectedInboxIds.value = filters.inboxIds || [];
  selectedTeamIds.value = filters.teamIds || [];
  selectedLabelIds.value = filters.labelIds || [];
  excludeLabelIds.value = filters.excludeLabelIds || [];
  resolutionMethod.value = filters.resolutionMethod || 'all';
  statusAtPeriodEnd.value = filters.statusAtPeriodEnd || 'all';
  reopening.value = filters.reopening || 'all';
  transfers.value = filters.transfers || 'all';
  humanResponse.value = filters.humanResponse || 'all';
};

applyEntityFilters(parseSummaryEntityFilterParams(route.query));

const allLabels = useMapGetter('labels/getLabels');

const chartFilters = computed(() => ({
  from: from.value,
  to: to.value,
  businessHours: businessHours.value,
  inboxIds: selectedInboxIds.value,
  teamIds: selectedTeamIds.value,
  labelIds: selectedLabelIds.value,
  excludeLabelIds: excludeLabelIds.value,
  agentIds: selectedAgentIds.value,
  resolutionMethod: resolutionMethod.value,
  statusAtPeriodEnd: statusAtPeriodEnd.value,
  reopening: reopening.value,
  transfers: transfers.value,
  humanResponse: humanResponse.value,
  comparisonPeriods: comparisonPeriods.value,
}));

const archiveContext = computed(() => {
  const labelTitles = selectedLabelIds.value
    .map(id => allLabels.value?.find(label => label.id === id)?.title)
    .filter(Boolean);
  const excludeLabelTitles = excludeLabelIds.value
    .map(id => allLabels.value?.find(label => label.id === id)?.title)
    .filter(Boolean);

  return {
    reportType: 'account',
    from: from.value,
    to: to.value,
    entityFilters: {
      agentIds: selectedAgentIds.value,
      inboxIds: selectedInboxIds.value,
      teamIds: selectedTeamIds.value,
      labelIds: selectedLabelIds.value,
      labelTitles,
      excludeLabelIds: excludeLabelIds.value,
      excludeLabelTitles,
      resolutionMethod: resolutionMethod.value,
      statusAtPeriodEnd: statusAtPeriodEnd.value,
      reopening: reopening.value,
      transfers: transfers.value,
      humanResponse: humanResponse.value,
    },
  };
});

const buildSummaryParams = () => {
  const params = {
    since: from.value,
    until: to.value,
    businessHours: businessHours.value,
  };
  if (selectedLabelIds.value.length) params.labelIds = selectedLabelIds.value;
  if (excludeLabelIds.value.length) {
    params.excludeLabelIds = excludeLabelIds.value;
  }
  if (selectedInboxIds.value.length) params.inboxIds = selectedInboxIds.value;
  if (selectedTeamIds.value.length) params.teamIds = selectedTeamIds.value;
  if (selectedAgentIds.value.length) params.agentIds = selectedAgentIds.value;
  if (resolutionMethod.value !== 'all') {
    params.resolutionMethod = resolutionMethod.value;
  }
  if (statusAtPeriodEnd.value !== 'all') {
    params.statusAtPeriodEnd = statusAtPeriodEnd.value;
  }
  if (reopening.value !== 'all') params.reopening = reopening.value;
  if (transfers.value !== 'all') params.transfers = transfers.value;
  if (humanResponse.value !== 'all') {
    params.humanResponse = humanResponse.value;
  }
  if (comparisonPeriods.value.length) {
    params.comparisonPeriods = comparisonPeriods.value;
  }
  return params;
};

const uiFlags = useMapGetter('summaryReports/getUIFlags');
const isLoading = computed(
  () =>
    uiFlags.value.isFetchingChatsReports ||
    uiFlags.value.isFetchingChatsTimeSummary
);

const chatsReport = useMapGetter('summaryReports/getChatsReport');
const chatsComparisons = useMapGetter('summaryReports/getChatsComparisons');
const chatsTimeSummary = useMapGetter('summaryReports/getChatsTimeSummary');

const metricCardData = computed(() => [chatsReport.value || {}]);
const comparisonSlices = computed(() =>
  (chatsComparisons.value || []).map(slice => ({
    ...slice,
    data: Array.isArray(slice.data) ? slice.data : [slice.data || {}],
  }))
);

let lastFetchKey = '';

const fetchReports = async () => {
  const params = buildSummaryParams();
  if (!params.since || !params.until) return;

  const fetchKey = JSON.stringify(params);
  if (fetchKey === lastFetchKey) return;
  lastFetchKey = fetchKey;

  try {
    await Promise.all([
      store.dispatch('summaryReports/fetchChatsReports', params),
      store.dispatch('summaryReports/fetchChatsTimeSummary', params),
    ]);
  } catch {
    lastFetchKey = '';
    useAlert(t('REPORT.SUMMARY_FETCHING_FAILED'));
  }
};

const onFiltersApply = ({ date, entity }) => {
  from.value = date.from;
  to.value = date.to;
  businessHours.value = false;
  comparisonPeriods.value = date.comparisonPeriods || [];
  applyEntityFilters(entity);
  fetchReports();
  useTrack(REPORTS_EVENTS.FILTER_REPORT, {
    filterValue: {
      from: from.value,
      to: to.value,
      businessHours: businessHours.value,
    },
    reportType: 'conversations',
  });
};

const onChartsLoaded = payload => {
  dailyDynamics.value = payload?.daily_dynamics || [];
};

const downloadReports = () => {
  const fileName = generateStatisticsFileName({
    section: 'chats',
    from: from.value,
    to: to.value,
  });
  store.dispatch('downloadChatsReports', {
    from: from.value,
    to: to.value,
    fileName,
    businessHours: businessHours.value,
    labelIds: selectedLabelIds.value,
    excludeLabelIds: excludeLabelIds.value,
    inboxIds: selectedInboxIds.value,
    teamIds: selectedTeamIds.value,
    agentIds: selectedAgentIds.value,
    resolutionMethod:
      resolutionMethod.value !== 'all' ? resolutionMethod.value : undefined,
    statusAtPeriodEnd:
      statusAtPeriodEnd.value !== 'all' ? statusAtPeriodEnd.value : undefined,
    reopening: reopening.value !== 'all' ? reopening.value : undefined,
    transfers: transfers.value !== 'all' ? transfers.value : undefined,
    humanResponse:
      humanResponse.value !== 'all' ? humanResponse.value : undefined,
    comparisonPeriods: comparisonPeriods.value,
  });
};

defineExpose({ downloadReports });
</script>

<template>
  <ReportFiltersSection
    type="chats"
    :disabled="isLoading"
    @filters-apply="onFiltersApply"
  />

  <section class="mt-2">
    <h3 class="px-1 text-sm font-medium text-n-slate-11">
      {{ $t('CHATS_REPORTS.SECTIONS.KPI') }}
    </h3>
    <SummaryMetricCards
      type="chats"
      :metrics="metricCardData"
      :archive-context="archiveContext"
      :primary-from="from"
      :primary-to="to"
      :comparison-slices="comparisonSlices"
    />
  </section>

  <ChatsTimeSummaryTable
    :summary="chatsTimeSummary"
    :archive-context="archiveContext"
  />
  <ChatsDailyDetailTable
    :rows="dailyDynamics"
    :archive-context="archiveContext"
  />

  <section class="mt-6">
    <h3 class="mb-2 px-1 text-sm font-medium text-n-slate-11">
      {{ $t('CHATS_REPORTS.SECTIONS.CYCLE_ANALYTICS') }}
    </h3>
    <ChatsReportCharts
      :filters="chartFilters"
      :archive-context="archiveContext"
      @charts-loaded="onChartsLoaded"
    />
  </section>

  <div
    v-if="isLoading"
    class="fixed inset-0 z-40 flex items-start justify-center pt-[12.5rem] bg-n-solid-1/40 pointer-events-none"
  >
    <Spinner :size="32" class="text-n-brand" />
  </div>
</template>
